import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, ClipboardList, FileText, Info, LayoutGrid, Printer, Sparkles, Thermometer, Truck, X } from "lucide-react";
import api, { formatApiErrorDetail, openServerPdf } from "../../lib/api";
import { formatItalianDateTime } from "../../lib/dateUtils";
import { setPrintPageSize, clearPrintPageSize } from "../../lib/printPageSize";
import { Card, Button, Field, Select, Spinner } from "../../components/ui-kit";

// Quadro sintetico mensile per il controllo ASL/NAS: righe = giorni del mese,
// colonne = unità frigo (temperature) o procedure (pulizie, altri controlli).
// Lo stesso componente disegna l'anteprima a schermo e il foglio A4 stampato.

const VIEWS = [
  { key: "temperature", label: "Temperature", icon: Thermometer },
  { key: "cleaning", label: "Pulizie", icon: Sparkles },
  { key: "other", label: "Altri controlli", icon: ClipboardList },
  { key: "vehicles", label: "Automezzi", icon: Truck },
  { key: "all", label: "Tutto", icon: LayoutGrid },
];
const PRINT_COLUMNS = { temperature: 12, cleaning: 9, other: 9, vehicles: 9 };

const CELL_LABEL = {
  ok: "Conforme", nc: "NON conforme", recorded: "Registrato (esito per unità non disponibile)", covered: "Già eseguito nel periodo",
  missing: "Previsto e non registrato", none: "Non previsto", closed: "Sede chiusa", future: "Giorno non ancora trascorso",
};

const currentMonth = () => new Date().toLocaleDateString("sv-SE").slice(0, 7);
const MONTH_NAMES = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
// Ultimi 24 mesi, dal più recente: un menu funziona allo stesso modo su ogni browser.
const monthOptions = () => {
  const [year, month] = currentMonth().split("-").map(Number);
  return Array.from({ length: 24 }, (_, index) => {
    const date = new Date(year, month - 1 - index, 1);
    return { value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`, label: `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}` };
  });
};

// Temperature: con la rilevazione analitica la cella mostra il valore inserito,
// con la conferma rapida la spunta verde = temperatura nell'intervallo previsto.
const TEMPERATURE_LABEL = { ok: "Temperatura rilevata nell'intervallo previsto", nc: "Temperatura fuori dall'intervallo previsto" };

function Cell({ cell, temperature }) {
  const state = cell?.s || "none";
  const values = cell?.v || [];
  const label = (temperature && TEMPERATURE_LABEL[state]) || CELL_LABEL[state];
  const title = `${label}${values.length ? `: ${values.join(" / ")}${temperature ? " °C" : ""}` : ""}${cell?.c ? " · rettificato" : ""}`;
  let content = null;
  if (state === "ok" || state === "nc" || state === "recorded") {
    content = values.length
      ? <span className="cs-values">{values.slice(0, 2).map((value, index) => <span key={index}>{value}{temperature ? "°" : ""}</span>)}</span>
      // Esito per esteso, non solo il simbolo.
      : state === "nc" ? <span className="cs-word"><X className="cs-icon" strokeWidth={3} />NON CONFORME</span>
        : state === "recorded" ? <span className="cs-word"><Check className="cs-icon" strokeWidth={3} />REGISTRATO</span>
          : <span className="cs-word"><Check className="cs-icon" strokeWidth={3} />CONFORME</span>;
  } else if (state === "covered") content = <Check className="cs-icon cs-icon-soft" strokeWidth={2} />;
  else if (state === "missing") content = <span className="cs-mark">!</span>;
  else if (state === "closed") content = <span className="cs-mark">C</span>;
  else if (state === "none") content = <span className="cs-word">NON PREVISTO</span>;
  return <td className={`cs-cell cs-${state}`} title={title}>{content}{cell?.c && <span className="cs-corrected" aria-hidden="true">R</span>}</td>;
}

// Modalità temperature presenti nel mese: valori (analitica) e/o spunte (conferma rapida).
const temperatureModes = (section) => {
  const cells = Object.values(section.cells || {}).flatMap((byDay) => Object.values(byDay || {}));
  const recorded = cells.filter((cell) => ["ok", "nc", "recorded"].includes(cell?.s));
  const values = recorded.some((cell) => (cell.v || []).length);
  const checks = recorded.some((cell) => !(cell.v || []).length);
  return { values, checks: checks || !values };
};

function Legend({ section }) {
  const temperature = section.key === "temperature";
  const modes = temperature ? temperatureModes(section) : null;
  const conformity = !temperature
    ? [["ok", <Check className="cs-icon" strokeWidth={3} />, "Conforme"], ["nc", <X className="cs-icon" strokeWidth={3} />, "Non conforme"]]
    : [
      ...(modes.checks ? [["ok", <Check className="cs-icon" strokeWidth={3} />, "CONFORME: temperatura rilevata nell'intervallo previsto"]] : []),
      ...(modes.values ? [["ok", "3,5°", modes.checks ? "Valore inserito, nell'intervallo previsto" : "Temperatura rilevata (valore inserito), nell'intervallo previsto"]] : []),
      ["nc", modes.values ? "9°" : <X className="cs-icon" strokeWidth={3} />, "Temperatura fuori dall'intervallo previsto"],
    ];
  const items = [...conformity,
    ["missing", "!", "Previsto, non registrato"], ["covered", <Check className="cs-icon cs-icon-soft" />, "Già eseguito nel periodo"], ["closed", "C", "Sede chiusa"], ["none", <span className="cs-word">NON PREVISTO</span>, "Non previsto"]];
  return <div className="cs-legend">{items.map(([state, sample, label], index) => <span key={`${state}-${index}`} className="cs-legend-item"><span className={`cs-cell cs-${state} cs-legend-swatch`}>{sample}</span>{label}</span>)}<span className="cs-legend-item"><span className="cs-legend-swatch cs-legend-r">R</span>Valore rettificato</span></div>;
}

// Nota "come leggere il quadro" per sezione: il cliente la ritrova in caso di
// ispezione. Stesso testo nel PDF del server (backend/conformity_summary_pdf.py).
const COMMON_ITEMS = [
  ["!", "previsto ma non registrato."],
  ["C", "sede chiusa."],
  ["NON PREVISTO", "in quel giorno non era programmato."],
];
const SECTION_NOTES = {
  temperature: {
    intro: "Ogni riga è un giorno del mese, ogni colonna è un frigorifero o un'altra unità di conservazione.",
    items: [
      ["CONFORME (o il valore in °C)", "la temperatura rilevata dall'OSA rientra nell'intervallo previsto per quell'unità."],
      ["NON CONFORME (o il valore in rosso)", "la temperatura è fuori dall'intervallo; l'azione correttiva è nell'elenco «Non conformità» in fondo."],
      ...COMMON_ITEMS,
      ["R", "il valore è stato corretto dopo la prima registrazione."],
    ],
  },
  cleaning: {
    intro: "Ogni colonna è una procedura di pulizia e sanificazione, con la sua frequenza (giornaliera, settimanale, mensile…).",
    items: [
      ["CONFORME", "la pulizia è stata eseguita e registrata in quel giorno."],
      ["Spunta chiara", "pulizia periodica già eseguita in un giorno precedente dello stesso periodo: non andava ripetuta."],
      ["NON CONFORME", "l'operatore ha registrato un'anomalia; l'azione correttiva è indicata in fondo."],
      ...COMMON_ITEMS,
    ],
  },
  other: {
    intro: "Ogni colonna è un controllo del piano di autocontrollo diverso da temperature e pulizie (es. ricevimento merci, infestanti, olio di frittura), con la sua frequenza.",
    items: [
      ["CONFORME", "il controllo è stato eseguito con esito regolare."],
      ["REGISTRATO", "il controllo è stato eseguito e salvato, senza un esito separato per quella voce."],
      ["Spunta chiara", "il controllo era già stato eseguito nel periodo previsto."],
      ["NON CONFORME", "è stata rilevata un'anomalia; l'azione correttiva è indicata in fondo."],
      ...COMMON_ITEMS,
    ],
  },
  vehicles: {
    intro: "Ogni colonna è un controllo di un mezzo di trasporto (carico, temperatura e pulizia del vano, impianto frigo), con la sua frequenza. I controlli «su richiesta» si registrano nei giorni in cui il mezzo viaggia.",
    items: [
      ["CONFORME", "il controllo è stato eseguito con esito conforme."],
      ["Spunta chiara", "il controllo periodico era già stato eseguito nel periodo previsto."],
      ["NON CONFORME", "è stata rilevata un'anomalia; l'azione correttiva è indicata in fondo."],
      ...COMMON_ITEMS,
    ],
  },
};
const STATS_NOTE = "Completezza = quante registrazioni previste sono state fatte · Esiti conformi = quante delle registrazioni fatte sono regolari · Un giorno senza registrazione non viene mai contato come conforme.";

function SectionNote({ section }) {
  const note = SECTION_NOTES[section.key];
  if (!note) return null;
  return <div className="cs-note" data-testid={`summary-note-${section.key}`}>
    <Info className="cs-note-icon" strokeWidth={2} aria-hidden="true" />
    <div>
      <p><strong>Come leggere questo quadro.</strong> {note.intro}</p>
      <ul>{note.items.map(([label, text]) => <li key={label}><strong>{label}</strong>: {text}</li>)}</ul>
    </div>
  </div>;
}

function Stats({ stats, ncCount }) {
  const tone = (value) => value == null ? "cs-stat-muted" : value >= 95 ? "cs-stat-good" : value >= 80 ? "cs-stat-warn" : "cs-stat-bad";
  return <div className="cs-stats">
    <div className={`cs-stat ${tone(stats.completion)}`}><strong>{stats.completion == null ? "—" : `${stats.completion}%`}</strong><span>Completezza registrazioni</span></div>
    <div className={`cs-stat ${tone(stats.conformity)}`}><strong>{stats.conformity == null ? "—" : `${stats.conformity}%`}</strong><span>Esiti conformi</span></div>
    <div className={`cs-stat ${ncCount ? "cs-stat-bad" : "cs-stat-good"}`}><strong>{ncCount}</strong><span>Non conformità</span></div>
    <div className={`cs-stat ${stats.missing ? "cs-stat-warn" : "cs-stat-good"}`}><strong>{stats.missing}</strong><span>Registrazioni mancanti</span></div>
  </div>;
}

function Matrix({ section, days, columns }) {
  const temperature = section.key === "temperature";
  return <table className="cs-table">
    <thead><tr>
      <th className="cs-day-head" scope="col">Giorno</th>
      {columns.map((column) => <th key={column.id} scope="col" className="cs-col-head">
        <span className="cs-col-name">{column.name}</span>
        <span className="cs-col-sub">{column.subtitle}</span>
        <span className="cs-col-detail">{column.detail}{column.historical ? " · storico" : ""}</span>
      </th>)}
    </tr></thead>
    <tbody>{days.map((day) => <tr key={day.date} className={day.weekend ? "cs-weekend" : ""}>
      <th scope="row" className="cs-day"><span className="cs-day-n">{day.day}</span><span className="cs-day-w">{day.weekday}</span></th>
      {columns.map((column) => <Cell key={column.id} cell={section.cells[column.id]?.[day.date]} temperature={temperature} />)}
    </tr>)}</tbody>
  </table>;
}

function SheetHeader({ data, section, part }) {
  return <header className="cs-sheet-head">
    <div>
      <p className="cs-sheet-kicker">Quadro sintetico delle conformità</p>
      <h3 className="cs-sheet-title">{section.title}{part ? ` (${part})` : ""}</h3>
      <p className="cs-sheet-meta">{data.tenant?.name}{data.tenant?.vat ? `, P. IVA ${data.tenant.vat}` : ""}</p>
      <p className="cs-sheet-meta">Sede: {data.site?.name}{data.site?.address ? `, ${data.site.address}` : ""}</p>
    </div>
    <div className="cs-sheet-month"><strong>{data.month_label}</strong><span>Generato il {formatItalianDateTime(data.generated_at)}</span></div>
  </header>;
}

function NonConformityList({ section }) {
  if (!section.nonconformities.length) return <p className="cs-nc-empty">Nessuna non conformità registrata nel mese.</p>;
  return <table className="cs-nc-table">
    <thead><tr><th>Data</th><th>{section.key === "temperature" ? "Unità e valore" : "Controllo"}</th><th>Cosa è stato riscontrato</th><th>Azione correttiva</th><th>Stato</th></tr></thead>
    <tbody>{section.nonconformities.map((row, index) => <tr key={index}><td>{row.date.split("-").reverse().join("-")}</td><td>{row.unit}</td><td>{row.finding}</td><td>{row.action || "Non indicata"}</td><td>{row.status}</td></tr>)}</tbody>
  </table>;
}

// Pagine A4 orizzontali: una per gruppo di colonne, più l'elenco NC se presente.
function PrintSheets({ data, sections }) {
  return sections.map((section) => {
    const size = PRINT_COLUMNS[section.key];
    const chunks = [];
    for (let index = 0; index < Math.max(section.columns.length, 1); index += size) chunks.push(section.columns.slice(index, index + size));
    return <React.Fragment key={section.key}>
      {chunks.map((columns, index) => <section key={index} className="cs-page">
        <SheetHeader data={data} section={section} part={chunks.length > 1 ? `parte ${index + 1} di ${chunks.length}` : ""} />
        <SectionNote section={section} />
        <Stats stats={section.stats} ncCount={section.nonconformities.length} />
        <p className="cs-stats-note">{STATS_NOTE}</p>
        {columns.length ? <Matrix section={section} days={data.days} columns={columns} /> : <p className="cs-nc-empty">Nessuna unità o controllo configurato per questa sede.</p>}
        <Legend section={section} />
        <footer className="cs-sign"><span>Responsabile dell’autocontrollo</span><span className="cs-sign-box">
          {data.signer?.signature?.startsWith("data:image") ? <img src={data.signer.signature} alt="Firma del responsabile" className="cs-sign-img" /> : <span className="cs-sign-missing">Firma non caricata</span>}
          {data.signer?.name && <span className="cs-sign-name">{data.signer.name}{data.signer.title ? ` — ${data.signer.title}` : ""}</span>}
        </span></footer>
      </section>)}
      {section.nonconformities.length > 0 && <section className="cs-page cs-page-flow">
        <SheetHeader data={data} section={{ ...section, title: `${section.title}: non conformità e azioni correttive` }} />
        <NonConformityList section={section} />
      </section>}
    </React.Fragment>;
  });
}

export default function ConformitySummary({ ctx }) {
  const sites = ctx.sites || [];
  const [month, setMonth] = useState(currentMonth());
  const [siteId, setSiteId] = useState(sites[0]?.id || "");
  const [view, setView] = useState("temperature");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(month) || month > currentMonth()) { setError("Seleziona un mese già iniziato."); setData(null); return; }
    let alive = true;
    setLoading(true); setError("");
    api.get("/app/reports/summary", { params: { month, ...(siteId ? { site_id: siteId } : {}) } })
      .then(({ data: result }) => { if (alive) setData(result); })
      .catch((err) => { if (alive) { setData(null); setError(formatApiErrorDetail(err.response?.data?.detail) || "Quadro non disponibile."); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month, siteId]);

  // La scheda Automezzi compare solo se la sede ha mezzi registrati.
  const views = VIEWS.filter(({ key }) => key !== "vehicles" || data?.sections.some((section) => section.key === "vehicles"));
  const visible = useMemo(() => !data ? [] : data.sections.filter((section) => view === "all" || section.key === view), [data, view]);

  // Report PDF generato dal server (vedi openServerPdf): si apre in una nuova scheda, pronto da salvare o inviare.
  const createReport = () => openServerPdf("/app/reports/summary/pdf", { month, view, ...(siteId ? { site_id: siteId } : {}) });

  // Stampa diretta della sezione visualizzata.
  const print = () => {
    setPrintPageSize(297, 210);
    document.body.setAttribute("data-print", "conformity-summary");
    setTimeout(() => { window.print(); document.body.removeAttribute("data-print"); clearPrintPageSize(); }, 80);
  };

  return <Card className="space-y-5 p-5 sm:p-6" data-testid="conformity-summary">
    <div className="flex flex-col gap-1">
      <h2 className="font-heading text-2xl font-semibold text-navy">Quadro sintetico delle conformità</h2>
      <p className="text-sm text-slate-500">Una pagina per il mese: ogni giorno con le unità frigo e i controlli, e l’esito registrato. Da mostrare a schermo, scaricare come report PDF o stampare.</p>
    </div>

    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[200px_minmax(0,1fr)_auto_auto] lg:items-end">
      <Field label="Mese"><Select value={month} onChange={(event) => setMonth(event.target.value)} aria-label="Mese del quadro" data-testid="summary-month">{monthOptions().map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</Select></Field>
      <Field label="Sede"><Select value={siteId} onChange={(event) => setSiteId(event.target.value)} aria-label="Sede del quadro" data-testid="summary-site">{sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</Select></Field>
      <Button variant="primary" onClick={createReport} disabled={!data || loading} data-testid="summary-create-report"><FileText className="h-4 w-4" />Crea report</Button>
      <Button variant="outline" onClick={print} disabled={!data || loading} data-testid="summary-print"><Printer className="h-4 w-4" />Stampa</Button>
    </div>

    <div className={`grid grid-cols-2 gap-2 ${views.length > 4 ? "sm:grid-cols-5" : "sm:grid-cols-4"}`} role="tablist" aria-label="Sezione del quadro">
      {views.map(({ key, label, icon: Icon }) => {
        const section = data?.sections.find((item) => item.key === key);
        const ncCount = key === "all" ? (data?.sections || []).reduce((sum, item) => sum + item.nonconformities.length, 0) : section?.nonconformities.length || 0;
        return <button key={key} type="button" role="tab" aria-selected={view === key} onClick={() => setView(key)} data-testid={`summary-view-${key}`}
          className={`flex min-h-[52px] items-center justify-center gap-2 rounded-xl border-2 px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-gold ${view === key ? "border-navy bg-navy text-white" : "border-slate-200 bg-white text-navy hover:border-navy/40"}`}>
          <Icon className="h-4 w-4" strokeWidth={1.8} />{label}
          {ncCount > 0 && <span className={`rounded-full px-2 py-0.5 text-xs ${view === key ? "bg-white text-red-700" : "bg-red-100 text-red-700"}`} title="Non conformità nel mese">{ncCount}</span>}
        </button>;
      })}
    </div>

    {loading && <div className="py-10"><Spinner /></div>}
    {!loading && error && <p role="alert" className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"><AlertTriangle className="h-4 w-4 shrink-0" />{error}</p>}
    {!loading && data && <div className="space-y-8">
      {visible.map((section) => <div key={section.key} className="space-y-3" data-testid={`summary-section-${section.key}`}>
        {view === "all" && <h3 className="font-heading text-xl font-semibold text-navy">{section.title}</h3>}
        <SectionNote section={section} />
        <Stats stats={section.stats} ncCount={section.nonconformities.length} />
        <p className="cs-stats-note">{STATS_NOTE}</p>
        {section.columns.length === 0
          ? <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">Nessuna {section.key === "temperature" ? "unità frigo" : "procedura"} configurata per questa sede.</p>
          : <div className="cs-scroll"><Matrix section={section} days={data.days} columns={section.columns} /></div>}
        <Legend section={section} />
        {section.nonconformities.length > 0 && <details className="rounded-xl border border-red-200 bg-red-50/40 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-red-800">Non conformità del mese e azioni correttive ({section.nonconformities.length})</summary>
          <div className="mt-3 overflow-x-auto"><NonConformityList section={section} /></div>
        </details>}
      </div>)}
      <p className="text-xs text-slate-500">Il quadro riporta solo le registrazioni effettivamente salvate: un giorno senza registrazione non viene mai indicato come conforme. Temperature: con la rilevazione analitica compare il valore inserito; con la conferma rapida la spunta verde indica che la temperatura rilevata rispetta l’intervallo previsto.</p>
    </div>}

    {data && createPortal(<div className="print-area conformity-summary-print"><PrintSheets data={data} sections={visible} /></div>, document.body)}
  </Card>;
}
